import { Client, ClientContact } from "@/Contracts/Clients";
import { Reducer } from "redux";

import {
	SalesActivityActionTypes,
	SalesActivityActions
} from "../../actions/SalesActivity";

import {
	AggregateSalesActivityResponse,
	SalesActivity,
	SalesActivityAutomation,
	SalesActivitySection,
	SalesActivityAutomationMetadataFilter,
	HistoryActivity,
	SalesActivityResponse,
	SelectedDocumentAndTemplate,
	ConflictedMeetigs
} from "@/Contracts/Others/SalesActivity";
import { RecentActivityFeedType } from "@/Contracts/Others/RecentActivityFeed";
import { Opportunity } from "@/Contracts/Opportunity";
import {
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata,
	SalesActivityUserViews,
	TableQueryFilter
} from "@/Contracts";
import { OpportunityStage } from "@/Contracts/Pipelines";
import { MergeTagVariable } from "@/Contracts/Documents";

export interface SalesActivityState {
	readonly isLoading: boolean;
	readonly salesActivityActiveTab: RecentActivityFeedType | null;
	readonly allSalesActivity: AggregateSalesActivityResponse;
	readonly notesSalesActivity: AggregateSalesActivityResponse;
	readonly tasksSalesActivity: AggregateSalesActivityResponse;
	readonly meetingsSalesActivity: AggregateSalesActivityResponse;
	readonly callsSalesActivity: AggregateSalesActivityResponse;
	readonly emailsSalesActivity: AggregateSalesActivityResponse;
	readonly historyActivity: HistoryActivity[];
	readonly currentActiveSalesActivity: SalesActivity | null;
	readonly currentActiveSalesActivityAutomation: SalesActivityAutomation | null;
	readonly salesActivityAutomationList: SalesActivityAutomation[];
	readonly salesActivityAutomationFiltersList: SalesActivityAutomationMetadataFilter[];
	readonly addCommentDrawerOpen: boolean;
	readonly editCommentDrawerOpen: boolean;
	readonly addTaskDrawerOpen: boolean;
	readonly editTaskDrawerOpen: boolean;
	readonly addMeetingDrawerOpen: boolean;
	readonly editMeetingDrawerOpen: boolean;
	readonly addLogEmailDrawerOpen: boolean;
	readonly editLogEmailDrawerOpen: boolean;
	readonly addLogCallDrawerOpen: boolean;
	readonly editLogCallDrawerOpen: boolean;
	readonly upcomingActivitiesDrawerOpen: boolean;
	readonly auditLogsDrawerOpen: boolean;
	readonly userAllSalesActivities: SalesActivityResponse;
	readonly opportunities: Opportunity[] | null;
	readonly opportunity_stages: OpportunityStage[] | null;
	readonly clients: Client[] | null;
	readonly clientContacts: ClientContact[] | null;
	readonly globalAddTaskDrawerOpen: boolean;
	readonly globalAddLogEmailDrawerOpen: boolean;
	readonly globalAddLogCallDrawerOpen: boolean;
	readonly globalAddMeetingDrawerOpen: boolean;
	readonly globalAddCommentDrawerOpen: boolean;
	readonly globalEditTaskDrawerOpen: boolean;
	readonly globalEditLogEmailDrawerOpen: boolean;
	readonly globalEditLogCallDrawerOpen: boolean;
	readonly globalEditMeetingDrawerOpen: boolean;
	readonly globalEditCommentDrawerOpen: boolean;
	readonly activeFilters: TableQueryFilter[];
	readonly salesMergeTags: MergeTagVariable[];
	readonly currentActiveSalesActivityAutomationActionIndex: number;
	readonly paymentDrawerOpen: boolean;
	readonly sendWebhookDrawerOpen: boolean;
	readonly selectedDocumentType: SelectedDocumentAndTemplate[];
	readonly viewMetadata: CustomViewsMetadata | null;
	readonly viewList: CustomViewResponse | null;
	readonly currentActiveView: CustomView | null;
	readonly isViewChanged: boolean;
	readonly conflictMeetings: ConflictedMeetigs[] | null;
	readonly selectedOwnerView: SalesActivityUserViews;
	readonly auditLogsCount: number | null;
}

export const initialSalesActivityState: SalesActivityState = {
	isLoading: false,
	salesActivityActiveTab: null,
	allSalesActivity: new AggregateSalesActivityResponse(),
	notesSalesActivity: new AggregateSalesActivityResponse(),
	tasksSalesActivity: new AggregateSalesActivityResponse(),
	meetingsSalesActivity: new AggregateSalesActivityResponse(),
	callsSalesActivity: new AggregateSalesActivityResponse(),
	emailsSalesActivity: new AggregateSalesActivityResponse(),
	historyActivity: new Array<HistoryActivity>(),
	currentActiveSalesActivity: null,
	currentActiveSalesActivityAutomation: null,
	salesActivityAutomationList: new Array<SalesActivityAutomation>(),
	salesActivityAutomationFiltersList:
		new Array<SalesActivityAutomationMetadataFilter>(),
	addCommentDrawerOpen: false,
	editCommentDrawerOpen: false,
	addTaskDrawerOpen: false,
	editTaskDrawerOpen: false,
	addMeetingDrawerOpen: false,
	editMeetingDrawerOpen: false,
	addLogEmailDrawerOpen: false,
	editLogEmailDrawerOpen: false,
	addLogCallDrawerOpen: false,
	editLogCallDrawerOpen: false,
	upcomingActivitiesDrawerOpen: false,
	auditLogsDrawerOpen: false,
	userAllSalesActivities: new SalesActivityResponse(),
	opportunities: null,
	opportunity_stages: null,
	clients: null,
	clientContacts: null,
	globalAddTaskDrawerOpen: false,
	globalAddLogEmailDrawerOpen: false,
	globalAddLogCallDrawerOpen: false,
	globalAddMeetingDrawerOpen: false,
	globalAddCommentDrawerOpen: false,
	globalEditTaskDrawerOpen: false,
	globalEditLogEmailDrawerOpen: false,
	globalEditLogCallDrawerOpen: false,
	globalEditMeetingDrawerOpen: false,
	globalEditCommentDrawerOpen: false,
	activeFilters: new Array<TableQueryFilter>(),
	salesMergeTags: new Array<MergeTagVariable>(),
	currentActiveSalesActivityAutomationActionIndex: 0,
	paymentDrawerOpen: false,
	sendWebhookDrawerOpen: false,
	selectedDocumentType: new Array<SelectedDocumentAndTemplate>(),
	viewMetadata: null,
	viewList: null,
	currentActiveView: null,
	isViewChanged: false,
	conflictMeetings: null,
	selectedOwnerView: SalesActivityUserViews.Owner,
	auditLogsCount: null
};

export const SalesActivityReducer: Reducer<
	SalesActivityState,
	SalesActivityActions
> = (
	state: SalesActivityState = initialSalesActivityState,
	action: SalesActivityActions
): SalesActivityState => {
	switch (action.type) {
		// Sales Activity Get Actions
		case SalesActivityActionTypes.SHOW_SALES_ACTIVITY_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case SalesActivityActionTypes.HIDE_SALES_ACTIVITY_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_ACTIVE_TAB_CHANGED: {
			return {
				...state,
				salesActivityActiveTab: action.salesActivityActiveTab
			};
		}
		case SalesActivityActionTypes.ALL_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_SALES_ACTIVITY_SUCCESSFUL: {
			const allSalesActivity = new AggregateSalesActivityResponse();
			if (allSalesActivity[SalesActivitySection.Overdue]) {
				if (action.paginate) {
					allSalesActivity[SalesActivitySection.Overdue].response = (
						state.allSalesActivity[SalesActivitySection.Overdue]
							? state.allSalesActivity[
									SalesActivitySection.Overdue
							  ]?.response
							: new Array<SalesActivity>()
					).concat(
						action.allSalesActivity[SalesActivitySection.Overdue]
							.response
					);
				} else {
					allSalesActivity[SalesActivitySection.Overdue].response =
						action.allSalesActivity[
							SalesActivitySection.Overdue
						]?.response;
				}
				allSalesActivity[SalesActivitySection.Overdue].next =
					action.allSalesActivity[SalesActivitySection.Overdue].next;
			}
			if (allSalesActivity[SalesActivitySection.Upcoming]) {
				if (action.paginate) {
					allSalesActivity[SalesActivitySection.Upcoming].response = (
						state.allSalesActivity[SalesActivitySection.Upcoming]
							? state.allSalesActivity[
									SalesActivitySection.Upcoming
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.allSalesActivity[SalesActivitySection.Upcoming]
							.response
					);
				} else {
					allSalesActivity[SalesActivitySection.Upcoming].response =
						action.allSalesActivity[
							SalesActivitySection.Upcoming
						].response;
				}
				allSalesActivity[SalesActivitySection.Upcoming].next =
					action.allSalesActivity[SalesActivitySection.Upcoming].next;
			}
			if (allSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					allSalesActivity[SalesActivitySection.Completed].response =
						(
							state.allSalesActivity[
								SalesActivitySection.Completed
							]
								? state.allSalesActivity[
										SalesActivitySection.Completed
								  ].response
								: new Array<SalesActivity>()
						).concat(
							action.allSalesActivity[
								SalesActivitySection.Completed
							].response
						);
				} else {
					allSalesActivity[SalesActivitySection.Completed].response =
						action.allSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				allSalesActivity[SalesActivitySection.Completed].next =
					action.allSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			return {
				...state,
				allSalesActivity
			};
		}
		case SalesActivityActionTypes.ALL_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_OVERDUE_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_OVERDUE_SALES_ACTIVITY_SUCCESSFUL: {
			const allSalesActivity = new AggregateSalesActivityResponse();
			if (allSalesActivity[SalesActivitySection.Overdue]) {
				if (action.paginate) {
					allSalesActivity[SalesActivitySection.Overdue].response = (
						state.allSalesActivity[SalesActivitySection.Overdue]
							? state.allSalesActivity[
									SalesActivitySection.Overdue
							  ]?.response
							: new Array<SalesActivity>()
					).concat(
						action.allSalesActivity[SalesActivitySection.Overdue]
							.response
					);
				} else {
					allSalesActivity[SalesActivitySection.Overdue].response =
						action.allSalesActivity[
							SalesActivitySection.Overdue
						]?.response;
				}
				allSalesActivity[SalesActivitySection.Overdue].next =
					action.allSalesActivity[SalesActivitySection.Overdue]?.next;
			}
			allSalesActivity[SalesActivitySection.Upcoming] =
				state.allSalesActivity[SalesActivitySection.Upcoming];
			allSalesActivity[SalesActivitySection.Completed] =
				state.allSalesActivity[SalesActivitySection.Completed];
			return {
				...state,
				allSalesActivity
			};
		}
		case SalesActivityActionTypes.ALL_OVERDUE_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_UPCOMING_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_UPCOMING_SALES_ACTIVITY_SUCCESSFUL: {
			const allSalesActivity = new AggregateSalesActivityResponse();
			if (allSalesActivity[SalesActivitySection.Upcoming]) {
				if (action.paginate) {
					allSalesActivity[SalesActivitySection.Upcoming].response = (
						state.allSalesActivity[SalesActivitySection.Upcoming]
							? state.allSalesActivity[
									SalesActivitySection.Upcoming
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.allSalesActivity[SalesActivitySection.Upcoming]
							.response
					);
				} else {
					allSalesActivity[SalesActivitySection.Upcoming].response =
						action.allSalesActivity[
							SalesActivitySection.Upcoming
						].response;
				}
				allSalesActivity[SalesActivitySection.Upcoming].next =
					action.allSalesActivity[SalesActivitySection.Upcoming].next;
			}
			allSalesActivity[SalesActivitySection.Overdue] =
				state.allSalesActivity[SalesActivitySection.Overdue];
			allSalesActivity[SalesActivitySection.Completed] =
				state.allSalesActivity[SalesActivitySection.Completed];
			return {
				...state,
				allSalesActivity
			};
		}
		case SalesActivityActionTypes.ALL_UPCOMING_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_COMPLETED_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_COMPLETED_SALES_ACTIVITY_SUCCESSFUL: {
			const allSalesActivity = new AggregateSalesActivityResponse();
			if (allSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					allSalesActivity[SalesActivitySection.Completed].response =
						(
							state.allSalesActivity[
								SalesActivitySection.Completed
							]
								? state.allSalesActivity[
										SalesActivitySection.Completed
								  ].response
								: new Array<SalesActivity>()
						).concat(
							action.allSalesActivity[
								SalesActivitySection.Completed
							].response
						);
				} else {
					allSalesActivity[SalesActivitySection.Completed].response =
						action.allSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				allSalesActivity[SalesActivitySection.Completed].next =
					action.allSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			allSalesActivity[SalesActivitySection.Overdue] =
				state.allSalesActivity[SalesActivitySection.Overdue];
			allSalesActivity[SalesActivitySection.Upcoming] =
				state.allSalesActivity[SalesActivitySection.Upcoming];
			return {
				...state,
				allSalesActivity
			};
		}
		case SalesActivityActionTypes.ALL_COMPLETED_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ALL_SALES_ACTIVITY_RESET: {
			return {
				...state,
				allSalesActivity: new AggregateSalesActivityResponse()
			};
		}
		case SalesActivityActionTypes.NOTES_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.NOTES_SALES_ACTIVITY_SUCCESSFUL: {
			const notesSalesActivity = new AggregateSalesActivityResponse();
			if (notesSalesActivity[SalesActivitySection.Pinned]) {
				if (action.paginate) {
					notesSalesActivity[SalesActivitySection.Pinned].response = (
						state.notesSalesActivity[SalesActivitySection.Pinned]
							? state.notesSalesActivity[
									SalesActivitySection.Pinned
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.notesSalesActivity[SalesActivitySection.Pinned]
							.response
					);
				} else {
					notesSalesActivity[SalesActivitySection.Pinned].response =
						action.notesSalesActivity[
							SalesActivitySection.Pinned
						].response;
				}
				notesSalesActivity[SalesActivitySection.Pinned].next =
					action.notesSalesActivity[SalesActivitySection.Pinned].next;
			}
			if (notesSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					notesSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.notesSalesActivity[SalesActivitySection.Completed]
							? state.notesSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.notesSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					notesSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.notesSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				notesSalesActivity[SalesActivitySection.Completed].next =
					action.notesSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			return {
				...state,
				notesSalesActivity
			};
		}
		case SalesActivityActionTypes.NOTES_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.NOTES_PINNED_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.NOTES_PINNED_SALES_ACTIVITY_SUCCESSFUL: {
			const notesSalesActivity = new AggregateSalesActivityResponse();
			if (notesSalesActivity[SalesActivitySection.Pinned]) {
				if (action.paginate) {
					notesSalesActivity[SalesActivitySection.Pinned].response = (
						state.notesSalesActivity[SalesActivitySection.Pinned]
							? state.notesSalesActivity[
									SalesActivitySection.Pinned
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.notesSalesActivity[SalesActivitySection.Pinned]
							.response
					);
				} else {
					notesSalesActivity[SalesActivitySection.Pinned].response =
						action.notesSalesActivity[
							SalesActivitySection.Pinned
						].response;
				}
				notesSalesActivity[SalesActivitySection.Pinned].next =
					action.notesSalesActivity[SalesActivitySection.Pinned].next;
			}
			notesSalesActivity[SalesActivitySection.Completed] =
				state.notesSalesActivity[SalesActivitySection.Completed];
			return {
				...state,
				notesSalesActivity
			};
		}
		case SalesActivityActionTypes.NOTES_PINNED_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.NOTES_COMPLETED_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.NOTES_COMPLETED_SALES_ACTIVITY_SUCCESSFUL: {
			const notesSalesActivity = new AggregateSalesActivityResponse();
			if (notesSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					notesSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.notesSalesActivity[SalesActivitySection.Completed]
							? state.notesSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.notesSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					notesSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.notesSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				notesSalesActivity[SalesActivitySection.Completed].next =
					action.notesSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			notesSalesActivity[SalesActivitySection.Pinned] =
				state.notesSalesActivity[SalesActivitySection.Pinned];
			return {
				...state,
				notesSalesActivity
			};
		}
		case SalesActivityActionTypes.NOTES_COMPLETED_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.NOTES_SALES_ACTIVITY_RESET: {
			return {
				...state,
				notesSalesActivity: new AggregateSalesActivityResponse()
			};
		}
		case SalesActivityActionTypes.TASKS_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_SALES_ACTIVITY_SUCCESSFUL: {
			const tasksSalesActivity = new AggregateSalesActivityResponse();
			if (tasksSalesActivity[SalesActivitySection.Overdue]) {
				if (action.paginate) {
					tasksSalesActivity[SalesActivitySection.Overdue].response =
						(
							state.tasksSalesActivity[
								SalesActivitySection.Overdue
							]
								? state.tasksSalesActivity[
										SalesActivitySection.Overdue
								  ].response
								: new Array<SalesActivity>()
						).concat(
							action.tasksSalesActivity[
								SalesActivitySection.Overdue
							].response
						);
				} else {
					tasksSalesActivity[SalesActivitySection.Overdue].response =
						action.tasksSalesActivity[
							SalesActivitySection.Overdue
						].response;
				}
				tasksSalesActivity[SalesActivitySection.Overdue].next =
					action.tasksSalesActivity[
						SalesActivitySection.Overdue
					].next;
			}
			if (tasksSalesActivity[SalesActivitySection.Upcoming]) {
				if (action.paginate) {
					tasksSalesActivity[SalesActivitySection.Upcoming].response =
						(
							state.tasksSalesActivity[
								SalesActivitySection.Upcoming
							]
								? state.tasksSalesActivity[
										SalesActivitySection.Upcoming
								  ].response
								: new Array<SalesActivity>()
						).concat(
							action.tasksSalesActivity[
								SalesActivitySection.Upcoming
							].response
						);
				} else {
					tasksSalesActivity[SalesActivitySection.Upcoming].response =
						action.tasksSalesActivity[
							SalesActivitySection.Upcoming
						].response;
				}
				tasksSalesActivity[SalesActivitySection.Upcoming].next =
					action.tasksSalesActivity[
						SalesActivitySection.Upcoming
					].next;
			}
			if (tasksSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					tasksSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.tasksSalesActivity[SalesActivitySection.Completed]
							? state.tasksSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.tasksSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					tasksSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.tasksSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				tasksSalesActivity[SalesActivitySection.Completed].next =
					action.tasksSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			return {
				...state,
				tasksSalesActivity
			};
		}
		case SalesActivityActionTypes.TASKS_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_OVERDUE_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_OVERDUE_SALES_ACTIVITY_SUCCESSFUL: {
			const tasksSalesActivity = new AggregateSalesActivityResponse();
			if (tasksSalesActivity[SalesActivitySection.Overdue]) {
				if (action.paginate) {
					tasksSalesActivity[SalesActivitySection.Overdue].response =
						(
							state.tasksSalesActivity[
								SalesActivitySection.Overdue
							]
								? state.tasksSalesActivity[
										SalesActivitySection.Overdue
								  ].response
								: new Array<SalesActivity>()
						).concat(
							action.tasksSalesActivity[
								SalesActivitySection.Overdue
							].response
						);
				} else {
					tasksSalesActivity[SalesActivitySection.Overdue].response =
						action.tasksSalesActivity[
							SalesActivitySection.Overdue
						].response;
				}
				tasksSalesActivity[SalesActivitySection.Overdue].next =
					action.tasksSalesActivity[
						SalesActivitySection.Overdue
					].next;
			}
			tasksSalesActivity[SalesActivitySection.Upcoming] =
				state.tasksSalesActivity[SalesActivitySection.Upcoming];
			tasksSalesActivity[SalesActivitySection.Completed] =
				state.tasksSalesActivity[SalesActivitySection.Completed];
			return {
				...state,
				tasksSalesActivity
			};
		}
		case SalesActivityActionTypes.TASKS_OVERDUE_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_UPCOMING_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_UPCOMING_SALES_ACTIVITY_SUCCESSFUL: {
			const tasksSalesActivity = new AggregateSalesActivityResponse();
			if (tasksSalesActivity[SalesActivitySection.Upcoming]) {
				if (action.paginate) {
					tasksSalesActivity[SalesActivitySection.Upcoming].response =
						(
							state.tasksSalesActivity[
								SalesActivitySection.Upcoming
							]
								? state.tasksSalesActivity[
										SalesActivitySection.Upcoming
								  ].response
								: new Array<SalesActivity>()
						).concat(
							action.tasksSalesActivity[
								SalesActivitySection.Upcoming
							].response
						);
				} else {
					tasksSalesActivity[SalesActivitySection.Upcoming].response =
						action.tasksSalesActivity[
							SalesActivitySection.Upcoming
						].response;
				}
				tasksSalesActivity[SalesActivitySection.Upcoming].next =
					action.tasksSalesActivity[
						SalesActivitySection.Upcoming
					].next;
			}
			tasksSalesActivity[SalesActivitySection.Overdue] =
				state.tasksSalesActivity[SalesActivitySection.Overdue];
			tasksSalesActivity[SalesActivitySection.Completed] =
				state.tasksSalesActivity[SalesActivitySection.Completed];
			return {
				...state,
				tasksSalesActivity
			};
		}
		case SalesActivityActionTypes.TASKS_UPCOMING_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_COMPLETED_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_COMPLETED_SALES_ACTIVITY_SUCCESSFUL: {
			const tasksSalesActivity = new AggregateSalesActivityResponse();
			if (tasksSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					tasksSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.tasksSalesActivity[SalesActivitySection.Completed]
							? state.tasksSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.tasksSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					tasksSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.tasksSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				tasksSalesActivity[SalesActivitySection.Completed].next =
					action.tasksSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			tasksSalesActivity[SalesActivitySection.Overdue] =
				state.tasksSalesActivity[SalesActivitySection.Overdue];
			tasksSalesActivity[SalesActivitySection.Upcoming] =
				state.tasksSalesActivity[SalesActivitySection.Upcoming];
			return {
				...state,
				tasksSalesActivity
			};
		}
		case SalesActivityActionTypes.TASKS_COMPLETED_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.TASKS_SALES_ACTIVITY_RESET: {
			return {
				...state,
				tasksSalesActivity: new AggregateSalesActivityResponse()
			};
		}
		case SalesActivityActionTypes.MEETINGS_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.MEETINGS_SALES_ACTIVITY_SUCCESSFUL: {
			const meetingsSalesActivity = new AggregateSalesActivityResponse();
			if (meetingsSalesActivity[SalesActivitySection.Upcoming]) {
				if (action.paginate) {
					meetingsSalesActivity[
						SalesActivitySection.Upcoming
					].response = (
						state.meetingsSalesActivity[
							SalesActivitySection.Upcoming
						]
							? state.meetingsSalesActivity[
									SalesActivitySection.Upcoming
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.meetingsSalesActivity[
							SalesActivitySection.Upcoming
						].response
					);
				} else {
					meetingsSalesActivity[
						SalesActivitySection.Upcoming
					].response =
						action.meetingsSalesActivity[
							SalesActivitySection.Upcoming
						].response;
				}
				meetingsSalesActivity[SalesActivitySection.Upcoming].next =
					action.meetingsSalesActivity[
						SalesActivitySection.Upcoming
					].next;
			}
			if (meetingsSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					meetingsSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.meetingsSalesActivity[
							SalesActivitySection.Completed
						]
							? state.meetingsSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.meetingsSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					meetingsSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.meetingsSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				meetingsSalesActivity[SalesActivitySection.Completed].next =
					action.meetingsSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			return {
				...state,
				meetingsSalesActivity
			};
		}
		case SalesActivityActionTypes.MEETINGS_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.MEETINGS_UPCOMING_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.MEETINGS_UPCOMING_SALES_ACTIVITY_SUCCESSFUL: {
			const meetingsSalesActivity = new AggregateSalesActivityResponse();
			if (meetingsSalesActivity[SalesActivitySection.Upcoming]) {
				if (action.paginate) {
					meetingsSalesActivity[
						SalesActivitySection.Upcoming
					].response = (
						state.meetingsSalesActivity[
							SalesActivitySection.Upcoming
						]
							? state.meetingsSalesActivity[
									SalesActivitySection.Upcoming
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.meetingsSalesActivity[
							SalesActivitySection.Upcoming
						].response
					);
				} else {
					meetingsSalesActivity[
						SalesActivitySection.Upcoming
					].response =
						action.meetingsSalesActivity[
							SalesActivitySection.Upcoming
						].response;
				}
				meetingsSalesActivity[SalesActivitySection.Upcoming].next =
					action.meetingsSalesActivity[
						SalesActivitySection.Upcoming
					].next;
			}
			meetingsSalesActivity[SalesActivitySection.Completed] =
				state.meetingsSalesActivity[SalesActivitySection.Completed];
			return {
				...state,
				meetingsSalesActivity
			};
		}
		case SalesActivityActionTypes.MEETINGS_UPCOMING_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.MEETINGS_COMPLETED_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.MEETINGS_COMPLETED_SALES_ACTIVITY_SUCCESSFUL: {
			const meetingsSalesActivity = new AggregateSalesActivityResponse();
			if (meetingsSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					meetingsSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.meetingsSalesActivity[
							SalesActivitySection.Completed
						]
							? state.meetingsSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.meetingsSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					meetingsSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.meetingsSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				meetingsSalesActivity[SalesActivitySection.Completed].next =
					action.meetingsSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			meetingsSalesActivity[SalesActivitySection.Upcoming] =
				state.meetingsSalesActivity[SalesActivitySection.Upcoming];
			return {
				...state,
				meetingsSalesActivity
			};
		}
		case SalesActivityActionTypes.MEETINGS_COMPLETED_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.MEETINGS_SALES_ACTIVITY_RESET: {
			return {
				...state,
				meetingsSalesActivity: new AggregateSalesActivityResponse()
			};
		}
		case SalesActivityActionTypes.CALLS_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.CALLS_SALES_ACTIVITY_SUCCESSFUL: {
			const callsSalesActivity = new AggregateSalesActivityResponse();
			if (callsSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					callsSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.callsSalesActivity[SalesActivitySection.Completed]
							? state.callsSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.callsSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					callsSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.callsSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				callsSalesActivity[SalesActivitySection.Completed].next =
					action.callsSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			return {
				...state,
				callsSalesActivity
			};
		}
		case SalesActivityActionTypes.CALLS_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.CALLS_SALES_ACTIVITY_RESET: {
			return {
				...state,
				callsSalesActivity: new AggregateSalesActivityResponse()
			};
		}
		case SalesActivityActionTypes.EMAILS_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.EMAILS_SALES_ACTIVITY_SUCCESSFUL: {
			const emailsSalesActivity = new AggregateSalesActivityResponse();
			if (emailsSalesActivity[SalesActivitySection.Completed]) {
				if (action.paginate) {
					emailsSalesActivity[
						SalesActivitySection.Completed
					].response = (
						state.emailsSalesActivity[
							SalesActivitySection.Completed
						]
							? state.emailsSalesActivity[
									SalesActivitySection.Completed
							  ].response
							: new Array<SalesActivity>()
					).concat(
						action.emailsSalesActivity[
							SalesActivitySection.Completed
						].response
					);
				} else {
					emailsSalesActivity[
						SalesActivitySection.Completed
					].response =
						action.emailsSalesActivity[
							SalesActivitySection.Completed
						].response;
				}
				emailsSalesActivity[SalesActivitySection.Completed].next =
					action.emailsSalesActivity[
						SalesActivitySection.Completed
					].next;
			}
			return {
				...state,
				emailsSalesActivity
			};
		}
		case SalesActivityActionTypes.EMAILS_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.EMAILS_SALES_ACTIVITY_RESET: {
			return {
				...state,
				emailsSalesActivity: new AggregateSalesActivityResponse()
			};
		}
		case SalesActivityActionTypes.UPDATE_CURRENT_ACTIVE_SALES_ACTIVITY: {
			return {
				...state,
				currentActiveSalesActivity: action.currentActiveSalesActivity
			};
		}
		// Sales Activity Delete / Add / Edit Actions
		case SalesActivityActionTypes.DELETE_COMMENT_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_COMMENT_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_COMMENT_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_COMMENT_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_COMMENT_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.ADD_COMMENT_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_COMMENT_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_COMMENT_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_COMMENT_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_TASK_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_TASK_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_TASK_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_TASK_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_TASK_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.ADD_TASK_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_TASK_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_TASK_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_TASK_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_MEETING_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_MEETING_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_MEETING_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_MEETING_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_MEETING_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.ADD_MEETING_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_MEETING_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_MEETING_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_MEETING_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_LOG_CALL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_LOG_CALL_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_LOG_CALL_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_LOG_CALL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_LOG_CALL_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.ADD_LOG_CALL_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_LOG_CALL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_LOG_CALL_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_LOG_CALL_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_LOG_EMAIL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_LOG_EMAIL_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_LOG_EMAIL_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_LOG_EMAIL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_LOG_EMAIL_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.ADD_LOG_EMAIL_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_LOG_EMAIL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_LOG_EMAIL_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_LOG_EMAIL_FAILED: {
			return state;
		}
		// Sales Activity Automation Actions
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATIONS_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATIONS_SUCCESSFUL: {
			return {
				...state,
				salesActivityAutomationList: action.salesActivityAutomationList
			};
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATIONS_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATION_METADATA_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATION_METADATA_SUCCESSFUL: {
			return {
				...state,
				salesActivityAutomationFiltersList:
					action.salesActivityAutomationFiltersList,
				opportunity_stages: action.opportunity_stages,
				salesMergeTags: action.salesActivityAutomationMergeTags
			};
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATION_METADATA_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATION_DETAILS_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATION_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveSalesActivityAutomation:
					action.currentActiveSalesActivityAutomation
			};
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATION_DETAILS_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.SALES_ACTIVITY_AUTOMATION_DETAILS_RESET: {
			return {
				...state,
				currentActiveSalesActivityAutomation: null
			};
		}
		case SalesActivityActionTypes.REORDER_SALES_ACTIVITY_AUTOMATION_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.REORDER_SALES_ACTIVITY_AUTOMATION_SUCCESSFUL: {
			return {
				...state,
				salesActivityAutomationList: action.salesActivityAutomationList
			};
		}
		case SalesActivityActionTypes.REORDER_SALES_ACTIVITY_AUTOMATION_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.RESET_SALES_ACTIVITY_AUTOMATIONS: {
			return {
				...state,
				salesActivityAutomationList:
					new Array<SalesActivityAutomation>()
			};
		}
		case SalesActivityActionTypes.DELETE_SALES_ACTIVITY_AUTOMATION_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_SALES_ACTIVITY_AUTOMATION_SUCCESSFUL: {
			return {
				...state,
				salesActivityAutomationList:
					state.salesActivityAutomationList.filter(
						salesActivityAutomation =>
							salesActivityAutomation.id !==
							action.deleteSalesActivityAutomation.id
					)
			};
		}
		case SalesActivityActionTypes.DELETE_SALES_ACTIVITY_AUTOMATION_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_SALES_ACTIVITY_AUTOMATION_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.ADD_SALES_ACTIVITY_AUTOMATION_SUCCESSFUL: {
			return {
				...state,
				salesActivityAutomationList: [
					action.addSalesActivityAutomation
				].concat(state.salesActivityAutomationList),
				currentActiveSalesActivityAutomation:
					state.currentActiveSalesActivityAutomation
						? action.addSalesActivityAutomation
						: null
			};
		}
		case SalesActivityActionTypes.ADD_SALES_ACTIVITY_AUTOMATION_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_SALES_ACTIVITY_AUTOMATION_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.EDIT_SALES_ACTIVITY_AUTOMATION_SUCCESSFUL: {
			const salesActivityAutomationList =
				state.salesActivityAutomationList.filter(
					salesActivityAutomation =>
						salesActivityAutomation.id !==
						action.editSalesActivityAutomation.id
				);
			const updateSalesActivityAutomationIndex =
				state.salesActivityAutomationList.findIndex(
					salesActivityAutomation =>
						salesActivityAutomation.id ===
						action.editSalesActivityAutomation.id
				);
			if (updateSalesActivityAutomationIndex > -1) {
				salesActivityAutomationList.splice(
					updateSalesActivityAutomationIndex,
					0,
					action.editSalesActivityAutomation
				);
			}
			return {
				...state,
				salesActivityAutomationList,
				currentActiveSalesActivityAutomation:
					state.currentActiveSalesActivityAutomation
						? action.editSalesActivityAutomation
						: null
			};
		}
		case SalesActivityActionTypes.EDIT_SALES_ACTIVITY_AUTOMATION_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.UPDATE_CURRENT_ACTIVE_SALES_ACTIVITY_AUTOMATION: {
			return {
				...state,
				currentActiveSalesActivityAutomation:
					action.currentActiveSalesActivityAutomation
			};
		}

		case SalesActivityActionTypes.HISTORY_ACTIVITIES_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.HISTORY_ACTIVITIES_SUCCESSFUL: {
			return {
				...state,
				historyActivity: [...state.historyActivity].concat(
					action.historyResponse
				)
			};
		}
		case SalesActivityActionTypes.HISTORY_ACTIVITIES_FAILED: {
			return state;
		}

		case SalesActivityActionTypes.HISTORY_ACTIVITIES_RESET: {
			return {
				...state,
				historyActivity: new Array<HistoryActivity>()
			};
		}

		case SalesActivityActionTypes.GET_ONE_MEETING_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_MEETING_SUCCESSFUL: {
			return {
				...state,
				currentActiveSalesActivity: action.salesActivity
			};
		}
		case SalesActivityActionTypes.GET_ONE_MEETING_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_TASK_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_TASK_SUCCESSFUL: {
			return {
				...state,
				currentActiveSalesActivity: action.salesActivity
			};
		}
		case SalesActivityActionTypes.GET_ONE_TASK_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_NOTE_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_NOTE_SUCCESSFUL: {
			return {
				...state,
				currentActiveSalesActivity: action.salesActivity
			};
		}
		case SalesActivityActionTypes.GET_ONE_NOTE_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_LOG_CALL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_LOG_CALL_SUCCESSFUL: {
			return {
				...state,
				currentActiveSalesActivity: action.salesActivity
			};
		}
		case SalesActivityActionTypes.GET_ONE_LOG_CALL_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_LOG_EMAIL_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_LOG_EMAIL_SUCCESSFUL: {
			return {
				...state,
				currentActiveSalesActivity: action.salesActivity
			};
		}
		case SalesActivityActionTypes.GET_ONE_LOG_EMAIL_FAILED: {
			return state;
		}

		// Sales Activity Drawer Actions
		case SalesActivityActionTypes.ADD_COMMENT_DRAWER_OPENED: {
			return {
				...state,
				addCommentDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_COMMENT_DRAWER_CLOSED: {
			return {
				...state,
				addCommentDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_COMMENT_DRAWER_OPENED: {
			return {
				...state,
				editCommentDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_COMMENT_DRAWER_CLOSED: {
			return {
				...state,
				editCommentDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_TASK_DRAWER_OPENED: {
			return {
				...state,
				addTaskDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_TASK_DRAWER_CLOSED: {
			return {
				...state,
				addTaskDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_TASK_DRAWER_OPENED: {
			return {
				...state,
				editTaskDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_TASK_DRAWER_CLOSED: {
			return {
				...state,
				editTaskDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_MEETING_DRAWER_OPENED: {
			return {
				...state,
				addMeetingDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_MEETING_DRAWER_CLOSED: {
			return {
				...state,
				addMeetingDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_MEETING_DRAWER_OPENED: {
			return {
				...state,
				editMeetingDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_MEETING_DRAWER_CLOSED: {
			return {
				...state,
				editMeetingDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_LOG_EMAIL_DRAWER_OPENED: {
			return {
				...state,
				addLogEmailDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_LOG_EMAIL_DRAWER_CLOSED: {
			return {
				...state,
				addLogEmailDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_LOG_EMAIL_DRAWER_OPENED: {
			return {
				...state,
				editLogEmailDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_LOG_EMAIL_DRAWER_CLOSED: {
			return {
				...state,
				editLogEmailDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_LOG_CALL_DRAWER_OPENED: {
			return {
				...state,
				addLogCallDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_LOG_CALL_DRAWER_CLOSED: {
			return {
				...state,
				addLogCallDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_LOG_CALL_DRAWER_OPENED: {
			return {
				...state,
				editLogCallDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_LOG_CALL_DRAWER_CLOSED: {
			return {
				...state,
				editLogCallDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.UPCOMING_ACTIVITIES_DRAWER_OPENED: {
			return {
				...state,
				upcomingActivitiesDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.UPCOMING_ACTIVITIES_DRAWER_CLOSED: {
			return {
				...state,
				upcomingActivitiesDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.AUDIT_LOGS_DRAWER_OPENED: {
			return {
				...state,
				auditLogsDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.AUDIT_LOGS_DRAWER_CLOSED: {
			return {
				...state,
				auditLogsDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.FETCH_OPPORTUNITIES_FOR_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_OPPORTUNITIES_FOR_SALES_ACTIVITY_SUCCESSFUL: {
			return {
				...state,
				opportunities: action.opportunities
			};
		}
		case SalesActivityActionTypes.FETCH_OPPORTUNITIES_FOR_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_CLIENTS_FOR_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_CLIENTS_FOR_SALES_ACTIVITY_SUCCESSFUL: {
			return {
				...state,
				clients: action.clients
			};
		}
		case SalesActivityActionTypes.FETCH_CLIENTS_FOR_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_CLIENT_CONTACTS_FOR_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_CLIENT_CONTACTS_FOR_SALES_ACTIVITY_SUCCESSFUL: {
			return {
				...state,
				clientContacts: action.clientContacts
			};
		}
		case SalesActivityActionTypes.FETCH_CLIENT_CONTACTS_FOR_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.RESET_SALES_ACTIVITY_OPPORTUNITIES: {
			return {
				...state,
				opportunities: null
			};
		}
		case SalesActivityActionTypes.RESET_SALES_ACTIVITY_CLIENTS: {
			return {
				...state,
				clients: null
			};
		}
		case SalesActivityActionTypes.RESET_SALES_ACTIVITY_CLIENT_CONTACTS: {
			return {
				...state,
				clientContacts: null
			};
		}
		case SalesActivityActionTypes.FETCH_USERS_ALL_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_USERS_ALL_SALES_ACTIVITY_SUCCESSFULLY: {
			return {
				...state,
				userAllSalesActivities: action.salesActivities
			};
		}
		case SalesActivityActionTypes.FETCH_USERS_ALL_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.RESET_USERS_ALL_SALES_ACTIVITY: {
			return {
				...state,
				userAllSalesActivities: new SalesActivityResponse()
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_COMMENT_DRAWER_OPENED: {
			return {
				...state,
				globalAddCommentDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_COMMENT_DRAWER_CLOSED: {
			return {
				...state,
				globalAddCommentDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_TASK_DRAWER_OPENED: {
			return {
				...state,
				globalAddTaskDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_TASK_DRAWER_CLOSED: {
			return {
				...state,
				globalAddTaskDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_MEETING_DRAWER_OPENED: {
			return {
				...state,
				globalAddMeetingDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_MEETING_DRAWER_CLOSED: {
			return {
				...state,
				globalAddMeetingDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_LOG_CALL_DRAWER_OPENED: {
			return {
				...state,
				globalAddLogCallDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_LOG_CALL_DRAWER_CLOSED: {
			return {
				...state,
				globalAddLogCallDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_LOG_EMAIL_DRAWER_OPENED: {
			return {
				...state,
				globalAddLogEmailDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.ADD_GLOBAL_LOG_EMAIL_DRAWER_CLOSED: {
			return {
				...state,
				globalAddLogEmailDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_COMMENT_DRAWER_OPENED: {
			return {
				...state,
				globalEditCommentDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_COMMENT_DRAWER_CLOSED: {
			return {
				...state,
				globalEditCommentDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_TASK_DRAWER_OPENED: {
			return {
				...state,
				globalEditTaskDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_TASK_DRAWER_CLOSED: {
			return {
				...state,
				globalEditTaskDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_MEETING_DRAWER_OPENED: {
			return {
				...state,
				globalEditMeetingDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_MEETING_DRAWER_CLOSED: {
			return {
				...state,
				globalEditMeetingDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_LOG_CALL_DRAWER_OPENED: {
			return {
				...state,
				globalEditLogCallDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_LOG_CALL_DRAWER_CLOSED: {
			return {
				...state,
				globalEditLogCallDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_LOG_EMAIL_DRAWER_OPENED: {
			return {
				...state,
				globalEditLogEmailDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.EDIT_GLOBAL_LOG_EMAIL_DRAWER_CLOSED: {
			return {
				...state,
				globalEditLogEmailDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.UPDATE_ACTIVE_FILTERS: {
			return {
				...state,
				activeFilters: action.activeFilters
			};
		}
		case SalesActivityActionTypes.ADD_ACTIVITY_TO_USERS_SALES_ACTIVITY: {
			return {
				...state,
				userAllSalesActivities: {
					...state.userAllSalesActivities,
					response: [action.salesActivity].concat(
						state.userAllSalesActivities.response
					)
				}
			};
		}
		case SalesActivityActionTypes.UPDATE_SINGLE_USERS_SALES_ACTIVITY: {
			return {
				...state,
				userAllSalesActivities: {
					...state.userAllSalesActivities,
					response: state.userAllSalesActivities.response.map(
						activity => {
							if (activity.id === action.salesActivity.id) {
								return action.salesActivity;
							}
							return activity;
						}
					)
				}
			};
		}
		case SalesActivityActionTypes.GET_ONE_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_SALES_ACTIVITY_SUCCESSFUL: {
			return state;
		}
		case SalesActivityActionTypes.GET_ONE_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_ONE_SALES_ACTIVITY_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_ONE_SALES_ACTIVITY_SUCCESSFULLY: {
			return {
				...state,
				userAllSalesActivities: {
					...state.userAllSalesActivities,
					response: state.userAllSalesActivities.response.filter(
						activity => activity.id !== action.salesActivityId
					)
				}
			};
		}
		case SalesActivityActionTypes.DELETE_ONE_SALES_ACTIVITY_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_ALL_PSA_AUDIT_LOGS_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_ALL_PSA_AUDIT_LOGS_SUCCESSFUL: {
			return {
				...state,
				historyActivity: action.auditLogs,
				auditLogsCount: action.count
			};
		}
		case SalesActivityActionTypes.FETCH_ALL_PSA_AUDIT_LOGS_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.RETRY_FAILED_AUDIT_LOG_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.RETRY_FAILED_AUDIT_LOG_SUCCESFUL: {
			return state;
		}
		case SalesActivityActionTypes.RETRY_FAILED_AUDIT_LOG_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.UPDATE_CURRENT_ACTIVE_SALES_ACTIVITY_AUTOMATION_ACTION_INDEX: {
			return {
				...state,
				currentActiveSalesActivityAutomationActionIndex: action.index
			};
		}
		case SalesActivityActionTypes.SHOW_PAYMENT_DRAWER: {
			return {
				...state,
				paymentDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.HIDE_PAYMENT_DRAWER: {
			return {
				...state,
				paymentDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.SHOW_WEBHOOK_DRAWER: {
			return {
				...state,
				sendWebhookDrawerOpen: true
			};
		}
		case SalesActivityActionTypes.HIDE_WEBHOOK_DRAWER: {
			return {
				...state,
				sendWebhookDrawerOpen: false
			};
		}
		case SalesActivityActionTypes.UPDATE_SELECTED_DOCUMENT_TYPE: {
			return {
				...state,
				selectedDocumentType: {
					...state.selectedDocumentType,
					[state.currentActiveSalesActivityAutomationActionIndex]:
						action.selectedDocumentType
				}
			};
		}
		case SalesActivityActionTypes.FETCH_SALES_ACTIVITY_AND_AUTOMATION_VIEW_METADATA_SUCCESSFUL: {
			return {
				...state,
				viewMetadata: action.metadata
			};
		}
		case SalesActivityActionTypes.FETCH_SALES_ACTIVITY_AND_AUTOMATION_VIEWS_SUCCESSFUL: {
			return {
				...state,
				viewList: action.viewsList
			};
		}
		case SalesActivityActionTypes.UPDATE_SALES_ACTIVITY_AND_AUTOMATION_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: action.currentActiveView,
				isViewChanged: action.isViewChanged
			};
		}
		case SalesActivityActionTypes.CREATE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.CREATE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: [...(state.viewList?.data ?? []), action.view]
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case SalesActivityActionTypes.CREATE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.UPDATE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.UPDATE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.map(view =>
								view.id === action.view.id ? action.view : view
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case SalesActivityActionTypes.UPDATE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_REQUESTED: {
			return state;
		}
		case SalesActivityActionTypes.DELETE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.initialView,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.filter(
								view => view.id !== action.deletedView.id
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case SalesActivityActionTypes.DELETE_SALES_ACTIVITY_AND_AUTOMATION_VIEW_FAILED: {
			return state;
		}
		case SalesActivityActionTypes.FETCH_SALES_ACTIVITY_AND_AUTOMATION_LIST_SUCCESSFUL: {
			return {
				...state,
				salesActivityAutomationList: action.salesAutomationList.data
			};
		}
		case SalesActivityActionTypes.GET_CONFLICT_MEETING_DETAIL_SUCCESSFUL: {
			return {
				...state,
				conflictMeetings: action.conflictMeetings
			};
		}
		case SalesActivityActionTypes.UPDATE_SELECTED_OWNER: {
			return {
				...state,
				selectedOwnerView: action.selectedOwnerView
			};
		}
		case SalesActivityActionTypes.UPDATE_AUDIT_LOGS_COUNT: {
			return {
				...state,
				auditLogsCount: action.auditLogsCount
			};
		}

		default: {
			return state;
		}
	}
};

export default SalesActivityReducer;
